import React, { useState } from 'react';
import { Wallet, Plus, Trash2, TrendingDown, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

/**
 * Cenário Atual — permite simular a situação atual com:
 *  - Faturação própria + percentagem (simples ou por escalões)
 *  - Fee anual cobrado pela marca (subtrai)
 *  - Base / ajudas de custo anuais (soma)
 *  - 5 níveis de rede com percentagem configurável
 *
 *  Notifica o pai com o total calculado via onTotalChange.
 *  Todos os campos fazem select() no focus para permitir escrita direta.
 */

const focusSelect = (e) => e.target.select();

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(
    v || 0
  );

const CurrentScenario = ({ onTotalChange }) => {
  // Faturação própria
  const [ownRevenue, setOwnRevenue] = useState(50000);
  const [commissionMode, setCommissionMode] = useState('flat'); // 'flat' | 'brackets'
  const [ownPercent, setOwnPercent] = useState(60);
  const [brackets, setBrackets] = useState([
    { upTo: 30000, percent: 40 },
    { upTo: 50000, percent: 60 },
    { upTo: null, percent: 90 } // "acima de" — upTo null = infinito
  ]);

  // Fee e base
  const [annualFee, setAnnualFee] = useState(1500);
  const [baseAjudas, setBaseAjudas] = useState(0);

  // Níveis (0..4 = 1º..5º Grau)
  const [levels, setLevels] = useState([
    { count: 0, revenue: 30000, percent: 8 },
    { count: 0, revenue: 25000, percent: 4 },
    { count: 0, revenue: 20000, percent: 2 },
    { count: 0, revenue: 15000, percent: 1 },
    { count: 0, revenue: 10000, percent: 0.5 }
  ]);

  /* ----------------- Calculations ----------------- */
  const calcOwnCommission = () => {
    if (commissionMode === 'flat') {
      return (ownRevenue * ownPercent) / 100;
    }
    // Brackets — progressivo por escalões
    const sorted = [...brackets].sort((a, b) => {
      if (a.upTo === null) return 1;
      if (b.upTo === null) return -1;
      return a.upTo - b.upTo;
    });
    let remaining = ownRevenue;
    let prevCap = 0;
    let total = 0;
    for (const b of sorted) {
      if (remaining <= 0) break;
      const cap = b.upTo === null ? Infinity : b.upTo;
      const slice = Math.min(remaining, cap - prevCap);
      if (slice > 0) {
        total += (slice * (b.percent || 0)) / 100;
        remaining -= slice;
        prevCap = cap;
      }
    }
    return total;
  };

  const levelCommissions = levels.map(
    (l) => (l.count || 0) * (l.revenue || 0) * ((l.percent || 0) / 100)
  );
  const totalLevelCommission = levelCommissions.reduce((s, v) => s + v, 0);
  const ownCommission = calcOwnCommission();
  const grandTotal =
    ownCommission + totalLevelCommission + (baseAjudas || 0) - (annualFee || 0);

  React.useEffect(() => {
    if (typeof onTotalChange === 'function') onTotalChange(grandTotal);
  }, [grandTotal, onTotalChange]);

  /* ----------------- Bracket helpers ----------------- */
  const addBracket = () => {
    const next = [...brackets];
    // insert a new bracket before the "acima de" (null upTo) entry
    const lastIdx = next.findIndex((b) => b.upTo === null);
    const prev = lastIdx > 0 ? next[lastIdx - 1].upTo || 0 : 0;
    next.splice(lastIdx === -1 ? next.length : lastIdx, 0, {
      upTo: prev + 10000,
      percent: 50
    });
    setBrackets(next);
  };
  const removeBracket = (idx) => {
    if (brackets.length <= 2) return; // manter pelo menos 1 escalão + "acima"
    setBrackets(brackets.filter((_, i) => i !== idx));
  };
  const updateBracket = (idx, field, value) => {
    const next = brackets.map((b, i) =>
      i === idx ? { ...b, [field]: field === 'upTo' ? (value === '' ? null : Number(value)) : Number(value) } : b
    );
    setBrackets(next);
  };

  /* ----------------- Level helpers ----------------- */
  const updateLevel = (idx, field, value) => {
    const next = levels.map((l, i) =>
      i === idx ? { ...l, [field]: Number(value) || 0 } : l
    );
    setLevels(next);
  };

  return (
    <Card
      className="border-2 border-gray-500 bg-black/50 backdrop-blur-sm"
      data-testid="current-scenario-card"
    >
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Wallet className="h-5 w-5 text-gray-300" />
          Cenário Atual
        </CardTitle>
        <p className="text-gray-500 text-xs">
          Simule a situação atual (marca onde trabalha agora).
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ----- Faturação própria ----- */}
        <section className="space-y-3">
          <Label className="text-gray-300 font-semibold uppercase text-xs tracking-wider">
            Faturação Própria
          </Label>

          <div>
            <Label className="text-white text-xs">Faturação anual (€)</Label>
            <Input
              type="number"
              value={ownRevenue}
              onFocus={focusSelect}
              onChange={(e) => setOwnRevenue(Number(e.target.value) || 0)}
              className="bg-white/10 border-gray-500 text-white"
              data-testid="current-own-revenue"
            />
          </div>

          {/* Modo de comissão */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCommissionMode('flat')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                commissionMode === 'flat'
                  ? 'bg-gray-200 text-black'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid="current-mode-flat"
            >
              Percentagem Fixa
            </button>
            <button
              type="button"
              onClick={() => setCommissionMode('brackets')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                commissionMode === 'brackets'
                  ? 'bg-gray-200 text-black'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid="current-mode-brackets"
            >
              Por Escalões
            </button>
          </div>

          {commissionMode === 'flat' ? (
            <div>
              <Label className="text-white text-xs">Percentagem que recebe (%)</Label>
              <Input
                type="number"
                value={ownPercent}
                onFocus={focusSelect}
                onChange={(e) => setOwnPercent(Number(e.target.value) || 0)}
                className="bg-white/10 border-gray-500 text-white"
                data-testid="current-own-percent"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white text-xs">
                  Escalões (cálculo progressivo)
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addBracket}
                  className="h-7 text-gray-300 hover:text-white hover:bg-white/10"
                  data-testid="current-add-bracket"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Escalão
                </Button>
              </div>

              {brackets.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                  data-testid={`current-bracket-row-${i}`}
                >
                  <span className="text-xs text-gray-400 w-12 shrink-0">
                    {b.upTo === null ? 'acima' : 'até €'}
                  </span>
                  {b.upTo === null ? (
                    <span className="text-gray-300 text-sm flex-1">—</span>
                  ) : (
                    <Input
                      type="number"
                      value={b.upTo}
                      onFocus={focusSelect}
                      onChange={(e) => updateBracket(i, 'upTo', e.target.value)}
                      placeholder="€"
                      className="bg-white/10 border-gray-500 text-white h-9 text-sm flex-1"
                      data-testid={`current-bracket-upto-${i}`}
                    />
                  )}
                  <div className="flex items-center gap-1 w-24">
                    <Input
                      type="number"
                      value={b.percent}
                      onFocus={focusSelect}
                      onChange={(e) =>
                        updateBracket(i, 'percent', e.target.value)
                      }
                      className="bg-white/10 border-gray-500 text-white h-9 text-sm"
                      data-testid={`current-bracket-percent-${i}`}
                    />
                    <span className="text-gray-400 text-xs">%</span>
                  </div>
                  {b.upTo !== null && brackets.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeBracket(i)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      aria-label="Remover escalão"
                      data-testid={`current-bracket-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 italic">
                Ex: até €30.000 → 40% · até €50.000 → 60% · acima → 90%.
              </p>
            </div>
          )}
        </section>

        {/* ----- Fee ----- */}
        <section className="space-y-2 pt-4 border-t border-gray-700/60">
          <Label className="text-gray-300 font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
            <TrendingDown className="h-3.5 w-3.5" />
            Fee anual da marca (subtrai)
          </Label>
          <Input
            type="number"
            value={annualFee}
            onFocus={focusSelect}
            onChange={(e) => setAnnualFee(Number(e.target.value) || 0)}
            className="bg-white/10 border-gray-500 text-white"
            placeholder="€"
            data-testid="current-annual-fee"
          />
        </section>

        {/* ----- Base / ajudas ----- */}
        <section className="space-y-2">
          <Label className="text-gray-300 font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" />
            Base / Ajudas de custo (soma, €/ano)
          </Label>
          <Input
            type="number"
            value={baseAjudas}
            onFocus={focusSelect}
            onChange={(e) => setBaseAjudas(Number(e.target.value) || 0)}
            className="bg-white/10 border-gray-500 text-white"
            placeholder="€"
            data-testid="current-base-ajudas"
          />
        </section>

        {/* ----- Níveis ----- */}
        <section className="space-y-3 pt-4 border-t border-gray-700/60">
          <Label className="text-gray-300 font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
            <Users className="h-3.5 w-3.5" />
            Níveis de Rede da Marca Atual
          </Label>
          <p className="text-xs text-gray-500">
            Defina nº de pessoas, faturação média e % de comissão por nível.
          </p>

          {levels.map((l, i) => (
            <div
              key={i}
              className="border-l-2 border-gray-600 pl-3 space-y-2"
              data-testid={`current-level-row-${i + 1}`}
            >
              <Label className="text-gray-300 text-xs font-semibold">
                {i + 1}º Grau
              </Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-gray-500 text-[10px] uppercase">
                    Pessoas
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={l.count}
                    onFocus={focusSelect}
                    onChange={(e) => updateLevel(i, 'count', e.target.value)}
                    className="bg-white/10 border-gray-500 text-white h-8 text-sm"
                    data-testid={`current-level-count-${i + 1}`}
                  />
                </div>
                <div>
                  <Label className="text-gray-500 text-[10px] uppercase">
                    Faturação €
                  </Label>
                  <Input
                    type="number"
                    value={l.revenue}
                    onFocus={focusSelect}
                    onChange={(e) => updateLevel(i, 'revenue', e.target.value)}
                    className="bg-white/10 border-gray-500 text-white h-8 text-sm"
                    data-testid={`current-level-revenue-${i + 1}`}
                  />
                </div>
                <div>
                  <Label className="text-gray-500 text-[10px] uppercase">
                    %
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={l.percent}
                    onFocus={focusSelect}
                    onChange={(e) => updateLevel(i, 'percent', e.target.value)}
                    className="bg-white/10 border-gray-500 text-white h-8 text-sm"
                    data-testid={`current-level-percent-${i + 1}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* ----- Resumo ----- */}
        <section
          className="pt-6 border-t border-gray-700/60 space-y-2"
          data-testid="current-scenario-summary"
        >
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Comissão própria</span>
            <span className="text-white">{formatCurrency(ownCommission)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Comissões de rede</span>
            <span className="text-white">
              {formatCurrency(totalLevelCommission)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Base / ajudas</span>
            <span className="text-green-400">
              + {formatCurrency(baseAjudas)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Fee anual da marca</span>
            <span className="text-red-400">− {formatCurrency(annualFee)}</span>
          </div>

          <div className="pt-3 border-t border-gray-700/60">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
              Ganhos anuais atuais
            </div>
            <div
              className="text-3xl font-bold text-white heading-luxury"
              data-testid="current-scenario-total"
            >
              {formatCurrency(grandTotal)}
            </div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
};

export default CurrentScenario;
